import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Download } from "lucide-react";
import { Logo, ThemeToggle } from "@/components/Bits";
import { APP_LINKS } from "@/data/site";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "How it works", href: "#how" },
  { label: "Cities", href: "#cities" },
  { label: "Reviews", href: "#reviews" },
  { label: "Partner", href: "#partner" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        data-testid="nav-header"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 inset-x-0 z-50 px-4 sm:px-6 lg:px-10"
      >
        <div
          className={`mx-auto max-w-7xl mt-3 flex items-center justify-between rounded-2xl px-4 sm:px-5 py-2.5 transition-all duration-500 ${
            scrolled ? "nav-solid border border-azo-border shadow-azo" : "bg-transparent border border-transparent"
          }`}
        >
          <a href="#top" className="shrink-0" aria-label="AzoApp home" data-testid="nav-brand-link">
            <Logo className="h-12 sm:h-14 lg:h-14" testid="nav-brand-logo" />
          </a>

          <nav className="hidden lg:flex items-center gap-1">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.href.slice(1)}`}
                className="relative px-4 py-2 text-sm font-medium text-azo-muted hover:text-azo-text transition-colors duration-300 group"
              >
                {l.label}
                <span className="absolute left-4 right-4 -bottom-0.5 h-px scale-x-0 group-hover:scale-x-100 origin-left bg-azo-primary transition-transform duration-300" />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            <a
              href={APP_LINKS.customer}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="btn-download-customer-app-nav"
              className="relative hidden sm:inline-flex items-center gap-2 rounded-full bg-azo-primary text-white pl-4 pr-5 py-2.5 text-sm font-semibold font-heading hover:bg-azo-primaryh hover:-translate-y-0.5 transition-all duration-300 shadow-azo"
            >
              <span className="absolute inset-0 rounded-full ring-2 ring-azo-primary/40 animate-ping" style={{ animationDuration: "2.4s" }} />
              <Download size={16} /> Get the App
            </a>
            <button
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              data-testid="nav-mobile-open"
              className="lg:hidden h-10 w-10 grid place-items-center rounded-full border border-azo-border glass"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu"
          >
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 260 }}
              className="absolute right-0 top-0 h-full w-[82%] max-w-sm bg-azo-surface border-l border-azo-border p-6 flex flex-col"
            >
              <div className="flex items-center justify-between mb-10">
                <Logo className="h-10" testid="mobile-brand-logo" />
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Close menu"
                  data-testid="nav-mobile-close"
                  className="h-10 w-10 grid place-items-center rounded-full border border-azo-border"
                >
                  <X size={18} />
                </button>
              </div>
              <nav className="flex flex-col gap-1">
                {LINKS.map((l, i) => (
                  <motion.a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.06 }}
                    className="text-2xl font-heading font-semibold py-3 border-b border-azo-border/60 hover:text-azo-primary transition-colors"
                  >
                    {l.label}
                  </motion.a>
                ))}
              </nav>
              <a
                href={APP_LINKS.customer}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-auto inline-flex justify-center items-center gap-2 rounded-full bg-azo-primary text-white px-5 py-3.5 font-semibold font-heading"
              >
                <Download size={16} /> Download AzoApp
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
